<script setup lang="ts">
/**
 * Sélecteur de langue de donoor.org, à côté du « © Donoor » des pieds de page
 * (sombres). Discret : la langue est d'abord détectée automatiquement
 * (plugins/langue-navigateur.client.ts) ; ce menu sert à la corriger.
 * Popover ouvert au SURVOL (souris), au toucher (mobile) et au clavier, au-dessus
 * du bouton (bas de page).
 *
 * Le menu est téléporté dans <body>, en position fixe : les pieds de page sont
 * en `relative z-10`, un menu resté dedans passait SOUS la bannière cookies
 * (z-100) et la bulle du chat (z-50), qui flottent justement en bas d'écran.
 *
 * Mémorise le choix (cookie `donoor_langue`, 1 an) : il prime ensuite sur la
 * langue du navigateur. Une langue dans laquelle la page n'existe pas (le
 * blog, français seul) n'est pas proposée.
 *
 * Variante CLAIRE ouverte VERS LE BAS (01/11/2026, Karim) : en haut à droite
 * des pages de paiement, sur fond clair.
 */
import { COOKIE_LANGUE, LANGUES, NOM_LANGUE, type Langue } from "~/utils/langue";

const props = withDefaults(
  defineProps<{
    /** « sombre » : pieds de page ; « clair » : haut des pages de paiement. */
    variante?: "sombre" | "clair";
    /** Sens d'ouverture du menu. */
    sens?: "haut" | "bas";
  }>(),
  { variante: "sombre", sens: "haut" },
);

const MARGE_ECRAN = 8;

const { locale, t } = useI18n();
const switchLocalePath = useSwitchLocalePath();
const choix = useCookie<Langue | null>(COOKIE_LANGUE, {
  maxAge: 60 * 60 * 24 * 365,
  sameSite: "lax",
});

const bouton = ref<HTMLElement | null>(null);
const menu = ref<HTMLElement | null>(null);
const ouvert = ref(false);
const position = ref({ left: 0, bottom: 0, top: 0 });
let minuterieFermeture: ReturnType<typeof setTimeout> | undefined;

const options = computed(() =>
  LANGUES.map((code) => ({ code, label: NOM_LANGUE[code], cible: switchLocalePath(code) }))
    .filter((o) => o.cible),
);

// Centré au-dessus du bouton (pieds de page), ou sous lui aligné à droite
// (haut de page), sans sortir de l'écran (mobile).
function placer() {
  if (!bouton.value) return;
  const r = bouton.value.getBoundingClientRect();
  const largeur = menu.value?.offsetWidth ?? 176;
  const depart = props.sens === "bas" ? r.right - largeur : r.left + r.width / 2 - largeur / 2;
  position.value = {
    left: Math.min(Math.max(depart, MARGE_ECRAN), window.innerWidth - largeur - MARGE_ECRAN),
    bottom: window.innerHeight - r.top,
    top: r.bottom,
  };
}

function ouvrir() {
  clearTimeout(minuterieFermeture);
  if (ouvert.value) return;
  placer();
  ouvert.value = true;
  // Deuxième passe : la largeur réelle du menu n'est connue qu'une fois rendu.
  nextTick(placer);
}

function fermer() {
  clearTimeout(minuterieFermeture);
  ouvert.value = false;
}

// Délai : laisse le temps à la souris de passer du bouton au menu.
function fermerBientot() {
  clearTimeout(minuterieFermeture);
  minuterieFermeture = setTimeout(fermer, 150);
}

function basculer() {
  if (ouvert.value) fermer();
  else ouvrir();
}

function surPointeurEntre(e: PointerEvent) {
  if (e.pointerType === "mouse") ouvrir();
}

function surPointeurSort(e: PointerEvent) {
  if (e.pointerType === "mouse") fermerBientot();
}

function choisir(code: Langue) {
  choix.value = code;
  fermer();
}

function fermerSiDehors(e: Event) {
  const cible = e.target as Node;
  if (bouton.value?.contains(cible) || menu.value?.contains(cible)) return;
  fermer();
}

function fermerAuClavier(e: KeyboardEvent) {
  if (e.key !== "Escape" || !ouvert.value) return;
  fermer();
  bouton.value?.focus();
}

onMounted(() => {
  document.addEventListener("pointerdown", fermerSiDehors);
  document.addEventListener("keydown", fermerAuClavier);
  window.addEventListener("scroll", placer, { passive: true });
  window.addEventListener("resize", placer, { passive: true });
});
onUnmounted(() => {
  document.removeEventListener("pointerdown", fermerSiDehors);
  document.removeEventListener("keydown", fermerAuClavier);
  window.removeEventListener("scroll", placer);
  window.removeEventListener("resize", placer);
  clearTimeout(minuterieFermeture);
});
</script>

<template>
  <button
    ref="bouton"
    type="button"
    class="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-sm font-medium transition-colors cursor-pointer"
    :class="
      variante === 'clair'
        ? 'bg-white/90 backdrop-blur text-gray-700 shadow-sm ring-1 ring-gray-200 hover:bg-white hover:text-gray-900'
        : 'text-slate-400 hover:bg-white/5 hover:text-white'
    "
    :aria-label="t('commun.langue.changer')"
    aria-haspopup="menu"
    :aria-expanded="ouvert"
    @click="basculer"
    @pointerenter="surPointeurEntre"
    @pointerleave="surPointeurSort"
  >
    <DrapeauLangue :code="locale as Langue" />
    {{ NOM_LANGUE[locale as Langue] ?? locale }}
    <UIcon
      :name="sens === 'bas' ? 'i-lucide-chevron-down' : 'i-lucide-chevron-up'"
      class="size-3.5 transition-transform duration-200"
      :class="{ 'rotate-180': ouvert }"
      aria-hidden="true"
    />
  </button>

  <Teleport to="body">
    <Transition
      enter-active-class="transition duration-150 ease-out"
      :enter-from-class="sens === 'bas' ? 'opacity-0 -translate-y-1' : 'opacity-0 translate-y-1'"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition duration-100 ease-in"
      leave-from-class="opacity-100 translate-y-0"
      :leave-to-class="sens === 'bas' ? 'opacity-0 -translate-y-1' : 'opacity-0 translate-y-1'"
    >
      <!-- z-[110] : devant la bannière cookies (z-100) et la bulle du chat (50).
           pb-2 en padding (pas en margin) : la souris traverse l'espace entre
           le bouton et le menu sans quitter la zone survolée. -->
      <div
        v-if="ouvert"
        ref="menu"
        class="fixed z-[110]"
        :class="sens === 'bas' ? 'pt-2' : 'pb-2'"
        :style="
          sens === 'bas'
            ? { left: `${position.left}px`, top: `${position.top}px` }
            : { left: `${position.left}px`, bottom: `${position.bottom}px` }
        "
        @pointerenter="surPointeurEntre"
        @pointerleave="surPointeurSort"
      >
        <div
          role="menu"
          class="min-w-44 rounded-2xl bg-white py-1.5 shadow-lg shadow-black/10 ring-1 ring-gray-200"
        >
          <!-- `external` : VRAI rechargement de la page dans la nouvelle langue.
               Une navigation interne gardait les textes de l'association
               chargés dans l'ancienne langue (14/10/2026). -->
          <NuxtLink
            v-for="opt in options"
            :key="opt.code"
            :to="opt.cible"
            external
            role="menuitem"
            :hreflang="opt.code"
            :lang="opt.code"
            active-class="langue-lien-actif"
            exact-active-class="langue-lien-actif"
            aria-current-value="true"
            class="flex items-center gap-2.5 px-4 py-2.5 text-sm transition-colors no-underline hover:bg-gray-50"
            :class="opt.code === locale ? 'font-semibold text-main' : 'font-medium text-gray-800'"
            @click="choisir(opt.code)"
          >
            <DrapeauLangue :code="opt.code" />
            <span class="flex-1">{{ opt.label }}</span>
            <UIcon
              v-if="opt.code === locale"
              name="i-lucide-check"
              class="size-4 text-main"
              aria-hidden="true"
            />
          </NuxtLink>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
